import React, { useState } from 'react';
import { Input, InputNumber, Button, Modal, Spin, Table } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSalaryAdjustForm } from './hooks/useSalaryAdjustForm';
import LookupField from '../../components/LookupField';

const P9234Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    empCode, setEmpCode, month, setMonth,
    laborIns, setLaborIns, healthIns, setHealthIns,
    deptCode, setDeptCode,
    details, addDetail, removeDetail, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useSalaryAdjustForm();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除調整單「${current.f9440_01}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) quickSearch(searchText.trim());
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  // BR-C02: 差額 = 調整後金額 - 原有金額
  const columns = [
    {
      title: '序號', dataIndex: 'f9441_02', key: 'seq', width: 60,
      render: (_: number, __: any, idx: number) => idx + 1,
    },
    {
      title: '薪資代號', dataIndex: 'f9441_11', key: 'code', width: 120,
      render: (val: string, _: any, idx: number) =>
        isEditing ? (
          <Input size="small" value={val} maxLength={20}
            onChange={e => updateDetail(idx, 'f9441_11', e.target.value)} />
        ) : val,
    },
    {
      title: '薪資名稱', dataIndex: 'f9441_11a', key: 'name', width: 150,
      render: (val: string, _: any, idx: number) =>
        isEditing ? (
          <Input size="small" value={val} maxLength={40}
            onChange={e => updateDetail(idx, 'f9441_11a', e.target.value)} />
        ) : val,
    },
    {
      title: '原有金額', dataIndex: 'f9441_21', key: 'oldAmt', width: 120,
      render: (val: number, _: any, idx: number) =>
        isEditing ? (
          <InputNumber size="small" value={val} style={{ width: '100%' }}
            onChange={v => updateDetail(idx, 'f9441_21', v ?? 0)} />
        ) : val?.toLocaleString(),
    },
    {
      title: '調整後金額', dataIndex: 'f9441_12', key: 'newAmt', width: 120,
      render: (val: number, _: any, idx: number) =>
        isEditing ? (
          <InputNumber size="small" value={val} style={{ width: '100%' }}
            onChange={v => updateDetail(idx, 'f9441_12', v ?? 0)} />
        ) : val?.toLocaleString(),
    },
    {
      title: '差額', key: 'diff', width: 100,
      render: (_: any, row: any) => {
        const diff = (row.f9441_12 || 0) - (row.f9441_21 || 0);
        return <span className={diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : ''}>
          {diff.toLocaleString()}
        </span>;
      },
    },
    ...(isEditing ? [{
      title: '', key: 'action', width: 50,
      render: (_: any, __: any, idx: number) => (
        <Button type="link" danger size="small" icon={<DeleteOutlined />}
          onClick={() => removeDetail(idx)} />
      ),
    }] : []),
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          薪資調整單 (pw9234f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="輸入單號快速查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch} disabled={isEditing}
            style={{ width: 300 }} maxLength={20} />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Header Fields */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">調整單號</label>
            <Input value={current?.f9440_01 || ''} disabled style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">員工代號</label>
            <Input value={empCode} onChange={e => setEmpCode(e.target.value)}
              disabled={!isEditing || mode === 'edit'} maxLength={20} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">部門代號</label>
            <Input value={deptCode} onChange={e => setDeptCode(e.target.value)}
              disabled={!isEditing} maxLength={20} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">異動原因</label>
            <Input value={(current as any)?.f9440_19 || ''} disabled={!isEditing} maxLength={30}
              onChange={e => {/* handled by hook if needed */}} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開始異動月份 (YYYYMM)</label>
            <Input value={month} onChange={e => setMonth(e.target.value)}
              disabled={!isEditing} maxLength={6} placeholder="例: 202601" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">勞保薪資</label>
            <LookupField
              lookupType="salaryTier"
              value={String(laborIns || '')}
              onChange={(code) => setLaborIns(Number(code) || 0)}
              disabled={!isEditing}
              style={{ width: '100%' }}
              title="搜尋勞保薪資級距"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">健保薪資</label>
            <LookupField
              lookupType="salaryTier"
              value={String(healthIns || '')}
              onChange={(code) => setHealthIns(Number(code) || 0)}
              disabled={!isEditing}
              style={{ width: '100%' }}
              title="搜尋健保薪資級距"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">健保負擔人數</label>
            <InputNumber value={(current as any)?.f9440_24 || 0} disabled={!isEditing}
              min={0} style={{ width: '100%' }} />
          </div>
        </div>

        {/* Audit Fields (read-only) */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4 space-y-1">
            <div>修改人員: {current.f9440_91} | 修改日期: {current.f9440_92 || '-'} | 修改時間: {current.f9440_93 || '-'}</div>
          </div>
        )}

        {/* Detail Table */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">薪資調整明細</span>
            {isEditing && (
              <Button size="small" icon={<PlusOutlined />} onClick={addDetail}>新增明細</Button>
            )}
          </div>
          <Table
            dataSource={details}
            columns={columns}
            rowKey={(_, idx) => String(idx)}
            pagination={false}
            size="small"
            bordered
            locale={{ emptyText: '無明細資料' }}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9234Form;
